import React, { useState } from 'react';
import {
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Container,
} from 'react-bootstrap';
import { useGetStocksQuery, useUpdateStockMutation } from '../../slices/stocksApiSlice';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import { toast } from 'react-toastify';

const StockNoteSearchReplaceScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [replacementText, setReplacementText] = useState('');
  const [previewResults, setPreviewResults] = useState([]);
  const [isReplacing, setIsReplacing] = useState(false);

  const { data: stocksData, isLoading, error } = useGetStocksQuery();
  const [updateStock] = useUpdateStockMutation();

  // Preview matches in notes
  const handleSearch = () => {
    if (!searchTerm.trim()) return;

    const matches = stocksData?.stocks
      ?.filter((stock) =>
        stock.note?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map((stock) => ({
        stockId: stock._id,
        date: stock.date,
        originalNote: stock.note,
      }));

    setPreviewResults(matches || []);
  };

  // Replace notes
  const handleReplace = async () => {
    if (!replacementText.trim()) return toast.error('Replacement text required');
    if (!searchTerm.trim()) return toast.error('Search term required');

    const affectedStocks = stocksData?.stocks?.filter((stock) =>
      stock.note?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!affectedStocks || affectedStocks.length === 0) {
      return toast.info('No matching notes found');
    }

    setIsReplacing(true);

    try {
      for (const stock of affectedStocks) {
        const updatedNote = stock.note.replace(
          new RegExp(searchTerm, 'gi'),
          replacementText
        );

        await updateStock({
          stockId: stock._id,
          updatedStock: {
            ...stock,
            note: updatedNote,
          },
        }).unwrap();
      }

      toast.success(`Replaced "${searchTerm}" with "${replacementText}" in notes`);
      setPreviewResults([]);
      setSearchTerm('');
      setReplacementText('');
    } catch (err) {
      toast.error(err?.data?.message || 'Update failed');
    } finally {
      setIsReplacing(false);
    }
  };

  return (
    <Container fluid>
      <Row>
        <Col md={12}>
          <h1>Search & Replace in Stock Notes</h1>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Search for text in notes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Col>
        <Col md={5}>
          <Form.Control
            type="text"
            placeholder="Replacement text"
            value={replacementText}
            onChange={(e) => setReplacementText(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Button
            variant="primary"
            onClick={handleSearch}
            className="me-2"
            disabled={!searchTerm.trim() || isReplacing}
          >
            Preview Matches
          </Button>
          <Button
            variant="success"
            onClick={handleReplace}
            disabled={!replacementText.trim() || isReplacing}
          >
            Replace All
          </Button>
        </Col>
      </Row>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'Error loading stocks'}</Message>
      ) : previewResults.length === 0 ? (
        <Alert variant="info">No matching notes found. Try a different search term.</Alert>
      ) : (
        <Table striped bordered hover responsive className="table-sm">
          <thead>
            <tr>
              <th>DATE</th>
              <th>ORIGINAL NOTE</th>
            </tr>
          </thead>
          <tbody>
            {previewResults.map((item, idx) => (
              <tr key={idx}>
                <td>{new Date(item.date).toLocaleDateString()}</td>
                <td>{item.originalNote}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default StockNoteSearchReplaceScreen;